import React from 'react';

import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { type ValueFormat, csvOf, csvValue, download, formatValue } from './cards';

export type PivotColumn = { key: string; label: string; format: ValueFormat };
export type PivotRow = { label: string; values: Record<string, number | null> };

type Props = {
  title: string;
  subtitle?: string;
  rowLabel: string;
  columns: Array<PivotColumn>;
  rows: Array<PivotRow>;
  total?: PivotRow;
  problem?: string | null;
  truncated?: boolean;
  csvName?: string;
  maxHeight?: number;
  nowrapRowLabel?: boolean;
};

export default function PivotTable({
  title,
  subtitle,
  rowLabel,
  columns,
  rows,
  total,
  problem,
  truncated,
  csvName,
  maxHeight = 520,
  nowrapRowLabel,
}: Props) {
  const rowLabelSx = nowrapRowLabel ? ({ whiteSpace: 'nowrap' } as const) : undefined;
  const exportCsv = () =>
    download(
      csvName ?? `${title}.csv`,
      csvOf(
        [rowLabel, ...columns.map((column) => column.label)],
        [...rows, ...(total ? [total] : [])].map((row) => [
          row.label,
          ...columns.map((column) => csvValue(row.values[column.key] ?? null, column.format)),
        ]),
      ),
    );
  return (
    <Paper data-lens-pdf-card variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {!problem && rows.length > 0 && (
          <Button size="small" variant="text" onClick={exportCsv}>
            CSV
          </Button>
        )}
      </Stack>
      {problem ? (
        <Alert severity="info">{problem}</Alert>
      ) : (
        <>
          {truncated && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              This table reached its row limit, so some rows are not shown and totals understate. Narrow the filters.
            </Alert>
          )}
          <TableContainer sx={{ maxHeight }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={rowLabelSx}>{rowLabel}</TableCell>
                  {columns.map((column) => (
                    <TableCell key={column.key} align="right">
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.label} hover>
                    <TableCell sx={rowLabelSx}>{row.label}</TableCell>
                    {columns.map((column) => (
                      <TableCell key={column.key} align="right">
                        {formatValue(row.values[column.key] ?? null, column.format)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {total && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{total.label}</TableCell>
                    {columns.map((column) => (
                      <TableCell key={column.key} align="right" sx={{ fontWeight: 600 }}>
                        {formatValue(total.values[column.key] ?? null, column.format)}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1}>
                      <Typography variant="body2" color="text.secondary">
                        No rows for these filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
}
